import {
  COMMERCE_ROLE_NAMES,
  EXTERNAL_OPERATOR_ROLE_NAMES,
} from '@/components/security/AppRoles';
import type { UserApi, UserAssignmentRef } from '../models';
import { USER_TYPE_LABELS, type UserTypeFilter } from '../enums/UserType';

const namedFrom = (
  items?: Array<UserAssignmentRef | string> | null
): string[] => {
  if (!items?.length) return [];
  return items
    .map((item) => (typeof item === 'string' ? item : item.name || item.scopeName || ''))
    .map((name) => name.trim())
    .filter(Boolean);
};

export const getUserType = (user: UserApi): UserTypeFilter | undefined => {
  const raw = (user.userType || user.restriction || '').toLowerCase();
  if (raw === 'commerce' || raw === 'comercio') return 'commerce';
  if (raw === 'external_operator') return 'external_operator';

  const roles = user.userRoles ?? [];

  // La restricción del rol es el mismo dato con el que filtra el back
  // (`prepareFiltersFindAll`), así que va primero: si se mirara solo el nombre
  // del rol, el filtro "Comercio" podría traer filas que la columna muestra
  // como "Operador" cuando falta la var de entorno con los nombres.
  const isCommerceRole = (userRole: (typeof roles)[number]) =>
    (userRole.role.restrictions ?? []).includes('comercio') ||
    COMMERCE_ROLE_NAMES.includes(userRole.role.name);

  if (roles.some(isCommerceRole)) {
    return 'commerce';
  }

  const isExternalOperatorRole = (userRole: (typeof roles)[number]) =>
    (userRole.role.restrictions ?? []).includes('external_operator') ||
    EXTERNAL_OPERATOR_ROLE_NAMES.includes(userRole.role.name);

  if (roles.some(isExternalOperatorRole)) {
    return 'external_operator';
  }
  if (roles.length > 0 || raw === 'operator' || raw === 'operador') {
    return 'operator';
  }
  return undefined;
};

export const getUserTypeLabel = (user: UserApi): string => {
  const type = getUserType(user);
  return type ? USER_TYPE_LABELS[type] : '—';
};

export interface UserAssignments {
  operators: string[];
  commerces: string[];
  includeAllOperators: boolean;
  includeAllCommerces: boolean;
}

export const getUserAssignments = (user: UserApi): UserAssignments => {
  const operators = namedFrom(
    user.operators ?? user.logisticOperators ?? user.assignedOperators
  );
  const commerces = namedFrom(user.commerces ?? user.assignedCommerces);

  let includeAllOperators = Boolean(user.includeAllOperators);
  let includeAllCommerces = Boolean(user.includeAllCommerces);

  for (const scope of user.scopes ?? []) {
    const name = (scope.name || scope.scopeName || '').trim();
    if (scope.scopeType === 'operator') {
      if (scope.scopeId == null) includeAllOperators = true;
      else if (name) operators.push(name);
    }
    if (scope.scopeType === 'commerce') {
      if (scope.scopeId == null) includeAllCommerces = true;
      else if (name) commerces.push(name);
    }
  }

  return {
    operators: [...new Set(operators)],
    commerces: [...new Set(commerces)],
    includeAllOperators,
    includeAllCommerces,
  };
};
